import { is, List } from "immutable";
import * as QueryString from "querystringify";
import * as React from "react";
import * as BS from "react-bootstrap";
import * as BT from "react-bootstrap-table";
import { connect } from "react-redux";
import { Link, RouteComponentProps, withRouter } from "react-router-dom";
import * as redux from "redux";
import { Filters, FilterType, ScheduleAbstractsModal, UpdateClassPopup, UpdateGroupModal, UpdateGroupPopup, UpdatePlacePopup, UpdateStatusModal, UpdateStatusPopup } from "./components";
import * as Actions from "../../../actions";
import * as Comp from "../../../components";
import { Paths } from "../../../constants";
import * as M from "../../../models";
import { Api, ApiEndpoint } from "../../../services/Api";
import Settings from "../../../settings";
import { AppState } from "../../../store";
import { DateHelper, DocumentHelper, ReactHelper, Utility } from "../../../utils";
import { AbstractsViewSwitcher } from "../components";

const mapStateToProps = (state: AppState, ownProps: OwnProps | undefined): ConnectedState => ({
    ...Comp.Base.DefaultListViewStatePropsMap(state, ownProps),
    abstracts: state.adminAbstracts.listAbstracts,
    loadingAbstracts: state.adminAbstracts.loadingListAbstracts,
    subscribedEvents: state.adminEvents.subscribedEvents,
    subscribedEventAbstracts: state.abstract.subscribedEventAbstracts,
});

const mapDispatchToProps = (dispatch: redux.Dispatch<AppState>): ConnectedDispatch => ({
    ...Comp.Base.DefaultDispatchPropsMap(dispatch),
    loadAbstracts: (eventId: number) => dispatch(Actions.AdminAbstract.loadListAbstractsForEvent(eventId)),
    removeAbstracts: (guids: string[]) => dispatch(Actions.AdminAbstract.removeAbstracts(guids)),
    subscribeToEventAbstracts: (eventId: number) => dispatch(Actions.Abstract.subscribeToEventAbstracts(eventId)),
    toggleAbstractsLoading: (loading: boolean) => dispatch(Actions.AdminAbstract.toggleLoadingListAbstracts(loading)),
    updateAbstracts: (updated: List<M.Abstract.AbstractListItem>) => dispatch(Actions.AdminAbstract.updateListAbstracts(updated)),
});

type OwnProps = RouteComponentProps<{}> & {
    event?: M.Abstract.AbstractEvent;
    eventId: number;
    loadingEvent: boolean;
};

type ConnectedState = Comp.Base.DefaultListViewConnectedState & {
    abstracts: List<M.Abstract.AbstractListItem>;
    loadingAbstracts: boolean;
    subscribedEventAbstracts: M.Generic.ResourceUpdated;
    subscribedEvents: M.Generic.ResourceUpdated;
};

type ConnectedDispatch = Comp.Base.DefaultConnectedDispatch & {
    loadAbstracts: (eventId: number) => void;
    removeAbstracts: (guids: string[]) => void;
    subscribeToEventAbstracts: (eventId: number) => void;
    toggleAbstractsLoading: (loading: boolean) => void;
    updateAbstracts: (updated: List<M.Abstract.AbstractListItem>) => void;
};

interface OwnState {
    abstracts: List<M.Abstract.AbstractListItem>;
    appliedFilters: M.Search.AppliedFilter[];
    doFilter: boolean;
    filteredAbstracts: List<IRow>;
    selectedIds: number[];
    classifications: M.Search.SearchFilter[];
    generatingExport: boolean;
    gradingGroups: M.Search.SearchFilter[];
    invitationResponses: M.Search.SearchFilter[];
    placements: M.Search.SearchFilter[];
    statuses: M.Search.SearchFilter[];
    types: M.Abstract.AbstractTypeFilter[];
}

interface UrlQuery {
    class?: string;
    group?: string;
    invitation?: string;
    place?: string;
    status?: string;
    type?: string;
    noAuthors?: string;
}

interface UrlQueryObject {
    classificationIds: number[];
    groupIds: number[];
    invitationIds: number[];
    placementIds: number[];
    statusIds: number[];
    typeIds: number[];
}

type IRow = M.Abstract.AbstractListItemEnhanced;

type CombinedProps = ConnectedState & ConnectedDispatch & OwnProps;

class ListComponent extends Comp.Base.BaseListViewComponent<CombinedProps, OwnState> {

    private classMap: {[index: number]: M.Search.SearchFilter} = {};
    private groupMap: {[index: number]: M.Search.SearchFilter} = {};
    private invitationResponseMap: {[index: number]: M.Search.SearchFilter} = {};
    private placeMap: {[index: number]: M.Search.SearchFilter} = {};
    private statusMap: {[index: number]: M.Search.SearchFilter} = {};
    private typeMap: {[index: number]: M.Abstract.AbstractTypeFilter} = {};

    private batchDeleteConfirm: Comp.Modal.ConfirmModal | null = null;
    private classUpdatePopup: UpdateClassPopup | null = null;
    private groupUpdateModal: UpdateGroupModal | null = null;
    private groupUpdatePopup: UpdateGroupPopup | null = null;
    private placeUpdatePopup: UpdatePlacePopup | null = null;
    private scheduleAbstractsModal: ScheduleAbstractsModal | null = null;
    private statusUpdateModal: UpdateStatusModal | null = null;
    private statusUpdatePopup: UpdateStatusPopup | null = null;

    public constructor(props: CombinedProps) {
        super(props);

        const invitationResponses = M.Generic.typeDefinitionsAsSearchFilter(M.Abstract.InvitationResponse.Values);
        this.invitationResponseMap = Utility.createNumberIndexMap(invitationResponses, (x) => x.id);

        const statuses = M.Generic.typeDefinitionsAsSearchFilter(
            M.Abstract.AbstractStatuses.Values.filter((x) => x.id !== M.Abstract.AbstractStatuses.Deleted.id),
        );
        this.statusMap = Utility.createNumberIndexMap(statuses, (x) => x.id);

        this.state = {
            ...this.getInitialListViewState(props),
            abstracts: List(),
            appliedFilters: [],
            doFilter: false,
            filteredAbstracts: List(),
            classifications: [],
            generatingExport: false,
            gradingGroups: [],
            invitationResponses,
            placements: [],
            statuses,
            types: [],
            selectedIds: [],
        };
    }

    public async componentDidMount() {
        this._isMounted = true;
        this.props.subscribeToEventAbstracts(this.props.eventId);
        await this.initializeComponent();
    }

    public componentDidUpdate(prevProps: Readonly<CombinedProps>) {
        if (!is(prevProps.abstracts, this.props.abstracts))
            this.loadAbstractsFromState();

        if (!is(prevProps.event, this.props.event))
            this.initializeEvent();

        if (!is(prevProps.tableState, this.props.tableState))
            this.clearSelectedAbstractIds();

        if (!is(prevProps.subscribedEvents[this.props.eventId], this.props.subscribedEvents[this.props.eventId]))
            this.props.loadAbstracts(this.props.eventId);

        if (!is(prevProps.websocket, this.props.websocket))
            this.props.subscribeToEventAbstracts(this.props.eventId);

        if (!is(prevProps.subscribedEventAbstracts[this.props.eventId], this.props.subscribedEventAbstracts[this.props.eventId]))
            this.props.loadAbstracts(this.props.eventId);

        // just in case a different guid in URL has been detected
        if (prevProps.eventId !== this.props.eventId) {
            this.clearState(() => {
                this.initializeComponent();
            });
        }
    }

    public componentWillUnmount() {
        this._isMounted = false;
    }

    public shouldComponentUpdate?(nextProps: Readonly<OwnProps>, nextState: Readonly<OwnState>, nextContext: any): boolean {
        // console.log(nextState.doFilter, nextProps.location.search, this.props.location.search);
        if (nextState.doFilter) {
            this.filterAbstracts();
            return false;
        }

        if (nextProps.location.search !== this.props.location.search) {
            this.updateAllFilterChecks(nextProps.location.search);
            return false;
        }

        return true;
    }

    public render() {
        const eventId = this.props.eventId;
        const loading = this.state.loading || !!this.props.loadingEvent[eventId] || this.props.loadingAbstracts;
        const authorsHidden = this.isAuthorsHidden();

        const actionsCommon = <AbstractsViewSwitcher eventId={eventId} view="Abstracts" />;

        const actionsPrimary = <BS.ButtonToolbar>
            <BS.ButtonGroup>
                <BS.DropdownButton id="export-buttons" title={this.state.generatingExport ? <><Comp.Generic.LoadingIcon /> Exporting&hellip;</> : "Export"}>
                    <BS.MenuItem onClick={(e) => this.generateExport()} disabled={this.state.generatingExport}>
                        Abstracts
                    </BS.MenuItem>
                    <BS.MenuItem onClick={(e) => this.generateExportOfCME()} disabled={this.state.generatingExport}>
                        CME
                    </BS.MenuItem>
                    <BS.MenuItem onClick={(e) => this.generateExportOfGrades()} disabled={this.state.generatingExport}>
                        Grading report
                    </BS.MenuItem>
                    <BS.MenuItem onClick={(e) => this.generateExportOfReviewers()} disabled={this.state.generatingExport}>
                        Grading status
                    </BS.MenuItem>
                    <BS.MenuItem onClick={(e) => this.generateExportToPDF()} disabled={this.state.generatingExport}>
                        PDF
                    </BS.MenuItem>
                </BS.DropdownButton>
                <BS.Button
                    disabled={this.state.selectedIds.length === 0}
                    id="assign-group-btn"
                    onClick={() => this.onAssignGroupClick()
                }>
                    Assign to grading group
                </BS.Button>
                <BS.Button
                    disabled={this.state.selectedIds.length === 0}
                    id="batch-delete-btn"
                    onClick={() => this.onDeleteClick()
                }>
                    Delete
                </BS.Button>
            </BS.ButtonGroup>
            <BS.DropdownButton id="more-actions" title="More">
                <BS.MenuItem onClick={() => this.props.goto(Paths.AdminAbstractListGroups(eventId))}>Manage grading groups</BS.MenuItem>
                <BS.MenuItem onClick={() => this.toggleAuthorsColumn()}>{authorsHidden ? "Show" : "Hide"} authors</BS.MenuItem>
                <BS.MenuItem onClick={(e) => this.onUpdateStatusClick()} disabled={this.state.selectedIds.length === 0}>
                    Update status
                </BS.MenuItem>
                <BS.MenuItem onClick={(e) => this.onScheduleAbstractsClick()} disabled={this.state.selectedIds.length === 0}>
                    Schedule abstracts
                </BS.MenuItem>
                <BS.MenuItem onClick={() => this.props.goto(Paths.AdminAbstractAddNew(eventId))}>Add new abstract</BS.MenuItem>
            </BS.DropdownButton>
        </BS.ButtonToolbar>;

        const tableActions = this.getTableActions({
            dataLength: this.props.abstracts.size,
            filteredLength: this.state.filteredAbstracts.size,
            searchPlaceholder: "Search abstracts...",
        });

        return <Comp.Page.AdminPage actionsCommon={actionsCommon} actionsPrimary={actionsPrimary} actionsSecondary={tableActions} mainClass="results" title={"Abstracts"}>

            <Comp.Modal.ConfirmModal
                headerClassName="warning"
                onProceed={() => this.handleAbstractsBatchDelete()}
                title="Delete abstracts"
                renderBody={(data?: number[]) => {
                    if (!data)
                        return null;
                    return <p>Are you sure you want to delete the {data.length === 1 ? "" : data.length} selected abstract{data.length === 1 ? "" : "s"}?</p>;
                }}
                ref={(ref) => this.batchDeleteConfirm = ref}
            />

            <ScheduleAbstractsModal
                api={this.props.api}
                eventId={eventId}
                onUpdated={(ids, subEvt) => this.onAbstractsScheduled(ids, subEvt)}
                ref={(ref) => this.scheduleAbstractsModal = ref}
            />

            <UpdateGroupModal
                api={this.props.api}
                onUpdated={(updatedIds, selectedIds) => this.onAbstractGroupUpdated(updatedIds, selectedIds)}
                ref={(ref) => this.groupUpdateModal = ref}
            />

            <UpdateStatusModal
                api={this.props.api}
                onUpdated={(updatedIds, selectedIds) => this.onAbstractStatusUpdated(updatedIds, selectedIds)}
                ref={(ref) => this.statusUpdateModal = ref}
            />

            <UpdateClassPopup
                addNoneSelection="None"
                api={this.props.api}
                onError={(msg) => { this.toggleLoading(false); this.props.notifyError(msg); }}
                onProcessing={() => this.toggleLoading(true)}
                onUpdated={(updatedIds, selectedIds) => this.onAbstractClassUpdated(updatedIds, selectedIds)}
                ref={(ref) => this.classUpdatePopup = ref}
            />

            <UpdateGroupPopup
                addNoneSelection="None"
                api={this.props.api}
                onError={(msg) => { this.toggleLoading(false); this.props.notifyError(msg); }}
                onProcessing={() => this.toggleLoading(true)}
                onUpdated={(updatedIds, selectedIds) => this.onAbstractGroupUpdated(updatedIds, selectedIds)}
                ref={(ref) => this.groupUpdatePopup = ref}
            />

            <UpdatePlacePopup
                addNoneSelection="None"
                api={this.props.api}
                onError={(msg) => { this.toggleLoading(false); this.props.notifyError(msg); }}
                onProcessing={() => this.toggleLoading(true)}
                onUpdated={(updatedIds, selectedIds) => this.onAbstractPlaceUpdated(updatedIds, selectedIds)}
                ref={(ref) => this.placeUpdatePopup = ref}
            />

            <UpdateStatusPopup
                api={this.props.api}
                confirmationText={"Are you sure you want to update the status to {item}?"}
                onError={(msg) => { this.toggleLoading(false); this.props.notifyError(msg); }}
                onProcessing={() => this.toggleLoading(true)}
                onUpdated={(updatedIds, selectedIds) => this.onAbstractStatusUpdated(updatedIds, selectedIds)}
                promptConfirmation={true}
                ref={(ref) => this.statusUpdatePopup = ref}
            />

            {this.getErrorAlert()}

            <Comp.Generic.LoadingSpinner show={loading} />

            <Filters
                classifications={this.state.classifications}
                gradingGroups={this.state.gradingGroups}
                invitationResponses={this.state.invitationResponses}
                loadingEvent={!!this.props.loadingEvent[eventId]}
                placements={this.state.placements}
                statuses={this.state.statuses}
                types={this.state.types}
                onFilterSelect={(id, checked, type) => this.onFilterCheck(id, checked, type)}
            />

            <div className="results-list">

                <Comp.Search.AppliedFilters
                    filters={this.state.appliedFilters}
                    onClearAll={() => this.clearFilters()}
                    onClearFilter={(id, type) => this.onFilterCheck(id, false, type as FilterType)}
                />

                <BT.BootstrapTable
                    bordered={false}
                    data={this.state.filteredAbstracts.toArray()}
                    pagination={this.state.tablePageSize > -1}
                    selectRow={{
                        bgColor: "#f7f7f7",
                        className: "select-abstracts-checkbox",
                        mode: "checkbox",
                        selected: [...this.state.selectedIds],
                        onSelect: (row: M.Abstract.AbstractListItem, isSelected: boolean, event: any, rowIndex: number) => {
                            const selectedAbstractIds = [...this.state.selectedIds];
                            Utility.addToOrRemoveFromArray(selectedAbstractIds, row.id, isSelected);
                            this.setState({ ...this.state, selectedIds: selectedAbstractIds });
                        },
                        onSelectAll: (isSelected: boolean, rows: M.Abstract.AbstractListItem[]) => {
                            this.setState({ ...this.state, selectedIds: isSelected ? rows.map((r) => (r.id)) : [] });
                            return true;
                        },
                    }}
                    options={{
                        afterSearch: (search, results) => this.onTableSearch(search, results, () => this.afterSearch(search, results)),
                        defaultSortName: "id",
                        defaultSortOrder: "desc",
                        page: this.state.tablePage,
                        paginationPanel: () => <div />,
                        sizePerPage: this.state.tablePageSize,
                        onSortChange: (sortName: any, sortOrder: any) => this.handleTableSort(sortOrder, sortName),
                    }}
                    ref={(ref: any) => this.table = ref}
                >
                    <BT.TableHeaderColumn dataField="id" dataSort isKey hidden>ID</BT.TableHeaderColumn>

                    <BT.TableHeaderColumn dataField="idString" hidden searchable>ID</BT.TableHeaderColumn>

                    <BT.TableHeaderColumn
                        dataField="authorNames"
                        dataFormat={(cell: string, row: IRow) => (
                            <>
                                <span dangerouslySetInnerHTML={{ __html: cell || "" }} />
                                {row.invitationResponse
                                    ? <><br /><span className="mute">{row.invitationResponse}</span></>
                                    : null}
                            </>
                        )}
                        dataSort
                        hidden={authorsHidden}
                        width="14%"
                    >Authors</BT.TableHeaderColumn>

                    <BT.TableHeaderColumn
                        dataField="title"
                        dataFormat={(cell: any, row: IRow) => {
                            const date = M.Abstract.AbstractStatuses.isSubmitted(row) && row.submittedOn ? row.submittedOn : row.createdOn;
                            const datePrefix = M.Abstract.AbstractStatuses.isSubmitted(row) && row.submittedOn ? "Submitted" : "Created";
                            const noAuthors = this.isAuthorsHidden() ? "?noAuthors=1" : "";
                            const scheduled = row.scheduledOn.map((evt, i) => {
                                const startsOn = Utility.fromApiDateAsMoment(evt.startDate);
                                const endsOn = Utility.fromApiDateAsMoment(evt.endDate);
                                const day = startsOn.format("ddd MMM D");
                                const time = DateHelper.displayTimeRange(startsOn, endsOn, "-");
                                return <Comp.Generic.WrapperLess key={i}>
                                    <br /><Link to={Paths.AdminEventCalendarSubEventEdit(eventId, evt.parentId || 0, evt.id)}>Scheduled for {day}, {time}</Link>
                                </Comp.Generic.WrapperLess>;
                            });
                            return <Comp.Generic.WrapperLess>
                                <Link to={Paths.AdminAbstractDetails(row.eventId, row.guid) + noAuthors} dangerouslySetInnerHTML={{ __html: row.title }} />
                                <br />
                                <span className="mute">
                                    #{row.id} &nbsp;&nbsp; {datePrefix} {Utility.utcToLocalAsMoment(date).format("MMM D, YYYY")}
                                    {scheduled}
                                </span>
                            </Comp.Generic.WrapperLess>;
                        }}
                        dataSort
                    >Title</BT.TableHeaderColumn>

                    <BT.TableHeaderColumn
                        dataField="type"
                        dataFormat={(cell: any, row: IRow) => {
                            const map = this.typeMap[row.typeId];
                            if (map === undefined)
                                return row.placement || "";

                            const hasPlacement = map.placementIds.length > 0;
                            const placementDisplay = !hasPlacement
                                ? null
                                : <a className="updatable" href="#" onClick={(e) => this.displayPlaceUpdatePopup(e, row)}>{row.placement || "Placement..."}</a>;

                            return <span>
                                    {map.name}
                                    {(hasPlacement ? <br /> : null)}
                                    {placementDisplay}
                                </span>;
                        }}
                        dataSort
                        width="12%"
                    >Format</BT.TableHeaderColumn>

                    <BT.TableHeaderColumn
                        dataField="classification"
                        dataFormat={(cell: any, row: IRow) => {
                            const map = this.typeMap[row.typeId];
                            if (map === undefined)
                                return row.classification || "";

                            return map.classificationIds.length > 0
                                ? <a className="updatable" href="#" onClick={(e) => this.displayClassUpdatePopup(e, row)}>{row.classification || "Classification..."}</a>
                                : "";
                        }}
                        dataSort
                        width="22%"
                    >Classification</BT.TableHeaderColumn>

                    <BT.TableHeaderColumn
                        dataField="gradingGroup"
                        dataFormat={(cell: any, row: IRow) => (
                            <a className="updatable" href="#" onClick={(e) => this.displayGroupUpdatePopup(e, row)}>{row.gradingGroup || "Group..."}</a>
                        )}
                        dataSort
                        width="10%"
                    >Group</BT.TableHeaderColumn>

                    <BT.TableHeaderColumn
                        dataField="averageGrade"
                        dataFormat={(cell: any, row: IRow) => (
                            <>{row.averageGrade ? row.averageGrade.toFixed(2) : ""}</>
                        )}
                        dataSort
                        width="6%"
                    >Grade</BT.TableHeaderColumn>

                    <BT.TableHeaderColumn
                        dataField="status"
                        dataFormat={(cell: any, row: IRow) => (
                            <a className="updatable" href="#" onClick={(e) => this.displayStatusUpdatePopup(e, row)}>{row.status || "Status..."}</a>
                        )}
                        dataSort
                        width="9%"
                    >Status</BT.TableHeaderColumn>
                </BT.BootstrapTable>
            </div>

        </Comp.Page.AdminPage>;
    }

    private async afterSearch(search: string, results: ReadonlyArray<IRow>) {
        if (search) {
            await this.updateFilterCounts();
        }
        else {
            await this.filterAbstracts();
            await this.updateFilterCounts();
        }
    }

    /**
     * Triggers a clear of all currently applied filters
     */
    private clearFilters() {
        this.props.replaceUrlQuery({
            class: "",
            group: "",
            invitation: "",
            place: "",
            status: "",
            type: "",
        });
        this.saveTableState();
    }

    private async clearSelectedAbstractIds() {
        await this.setStateAsync({ ...this.state, selectedIds: [] });
    }

    private clearState(callback?: () => void) {
        this.setState({
            abstracts: List(),
            doFilter: false,
            filteredAbstracts: List(),
            selectedIds: [],
        }, callback);
    }

    private displayClassUpdatePopup(e: React.MouseEvent<HTMLAnchorElement>, abstract: IRow) {
        if (this.classUpdatePopup !== null) {
            const pos = Utility.getElementPosition(e.target as HTMLElement);
            const typeMap = this.typeMap[abstract.typeId];
            if (typeMap && this.props.event) {
                const classifications = this.props.event.classifications.filter((c) => typeMap.classificationIds.indexOf(c.id) > -1);
                this.classUpdatePopup.open(classifications, pos, abstract, abstract.classificationId);
            }
        }
        return ReactHelper.emptyClick(e);
    }

    private displayGroupUpdatePopup(e: React.MouseEvent<HTMLAnchorElement>, abstract: IRow) {
        if (this.groupUpdatePopup !== null) {
            const pos = Utility.getElementPosition(e.target as HTMLElement);
            this.groupUpdatePopup.open((this.props.event) ? [...this.props.event.gradingGroups] : [], pos, abstract, abstract.gradingGroupId);
        }

        e.preventDefault();
        return false;
    }

    private displayPlaceUpdatePopup(e: React.MouseEvent<HTMLAnchorElement>, abstract: IRow) {
        if (this.placeUpdatePopup !== null) {
            const pos = Utility.getElementPosition(e.target as HTMLElement);
            const typeMap = this.typeMap[abstract.typeId];
            if (typeMap && this.props.event) {
                const placements = this.props.event.placements.filter((p) => typeMap.placementIds.indexOf(p.id) > -1);
                this.placeUpdatePopup.open(placements, pos, abstract, abstract.placementId);
            }
        }
        return ReactHelper.emptyClick(e);
    }

    private displayStatusUpdatePopup(e: React.MouseEvent<HTMLAnchorElement>, abstract: IRow) {
        if (this.statusUpdatePopup !== null) {
            const pos = Utility.getElementPosition(e.target as HTMLElement);
            this.statusUpdatePopup.open((this.props.event) ? [...this.props.event.statuses] : [], pos, abstract, abstract.statusId);
        }

        e.preventDefault();
        return false;
    }

    private async filterAbstracts() {
        const filteredAbstracts: IRow[] = [];
        const q = this.getUrlQueryObject();
        const list = this.state.tableSearchResults
            ? List(this.state.tableSearchResults)
            : this.state.abstracts;

        list.forEach((x: M.Abstract.AbstractListItem) => {
            const gradingGroupId = x.gradingGroupId;
            const isFiltered = (!q.classificationIds.length && !q.groupIds.length && !q.invitationIds && !q.placementIds.length && !q.statusIds.length && !q.typeIds.length)
                || (
                    (!q.classificationIds.length || q.classificationIds.indexOf(x.classificationId || 0) > -1)
                    && (!q.groupIds.length || q.groupIds.indexOf(gradingGroupId || 0) > -1)
                    && (!q.invitationIds.length || (q.invitationIds.indexOf(x.invitationResponseId || 0) > -1) && !!x.invitationSent)
                    && (!q.placementIds.length || q.placementIds.indexOf(x.placementId || 0) > -1)
                    && (!q.statusIds.length || q.statusIds.indexOf(x.statusId) > -1)
                    && (!q.typeIds.length || q.typeIds.indexOf(x.typeId) > -1)
                );

            if (isFiltered) {
                const classification = this.classMap[x.classificationId || 0];
                const gradingGroup = this.groupMap[x.gradingGroupId || 0];
                const invitationResponse = this.invitationResponseMap[x.invitationResponseId || 0];
                const placement = this.placeMap[x.placementId || 0];
                const status = this.statusMap[x.statusId];
                const type = this.typeMap[x.typeId];

                const y: IRow = {
                    ...x,
                    idString: "#" + x.id + "#",
                    authorNames: this.formatAuthorsCol(x.authors),
                    classification: classification ? classification.name : "",
                    gradingGroup: gradingGroup ? gradingGroup.name : "",
                    invitationResponse: x.invitationSent
                        ? (`Invitation: ${invitationResponse ? invitationResponse.name : "No response"}`)
                        : undefined,
                    placement: placement ? placement.name : "",
                    status: status ? status.name : "",
                    type: type ? type.name : "",
                };
                filteredAbstracts.push(y);
            }
        });

        await this.setStateAsync({
            ...this.state,
            doFilter: false,
            filteredAbstracts: List(filteredAbstracts),
        });
    }

    private formatAuthorsCol(cell: M.Abstract.AbstractAuthor[]) {
        return cell
            .filter((a) => a.isPrimary)
            .map((a) => {
                return `${a.lastName}, ${(a.firstName || " ").substr(0, 1).toUpperCase()}.`;
            })
            .join("<br />");
    }

    private async generateExport() {
        if (this.state.generatingExport)
            return;

        await this.setStateAsync({ ...this.state, generatingExport: true });

        const urlQuery = this.getUrlQueryObject();
        const eventId = this.props.eventId;
        let abstractIds = this.state.selectedIds;

        // if filtering by search and no selected abstract IDs, get
        // all abstract IDs from search results
        if (this.state.tableSearchResults && !abstractIds.length)
            abstractIds = this.state.tableSearchResults.map((x: IRow) => x.id);

        const body = {
            abstractIds,
            classificationIds: urlQuery.classificationIds,
            eventId,
            groupIds: urlQuery.groupIds,
            invitationIds: urlQuery.invitationIds,
            placementIds: urlQuery.placementIds,
            statusIds: urlQuery.statusIds,
            typeIds: urlQuery.typeIds,
        };

        const result = await this.props.api.post(Api.endpoints.admin.abstract.exportAbstracts.withUrlParams({ eventId }), {
            body: JSON.stringify(body),
        });

        if (!this._isMounted)
            return;

        await this.setStateAsync({ ...this.state, generatingExport: false });
        this.getExportFile(result);
    }

    private async generateExportOfCME() {
        if (this.state.generatingExport)
            return;

        await this.setStateAsync({ ...this.state, generatingExport: true });

        const urlQuery = this.getUrlQueryObject();
        const eventId = this.props.eventId;
        let abstractIds = this.state.selectedIds;

        // if filtering by search and no selected abstract IDs, get
        // all abstract IDs from search results
        if (this.state.tableSearchResults && !abstractIds.length)
            abstractIds = this.state.tableSearchResults.map((x: IRow) => x.id);

        const body = {
            abstractIds,
            classificationIds: urlQuery.classificationIds,
            eventId,
            groupIds: urlQuery.groupIds,
            invitationIds: urlQuery.invitationIds,
            placementIds: urlQuery.placementIds,
            statusIds: urlQuery.statusIds,
            typeIds: urlQuery.typeIds,
        };

        const result = await this.props.api.post(Api.endpoints.admin.abstract.exportAbstractCME.withUrlParams({ eventId }), {
            body: JSON.stringify(body),
        });

        if (!this._isMounted)
            return;

        await this.setStateAsync({ ...this.state, generatingExport: false });
        this.getExportFile(result);
    }

    private async generateExportOfGrades() {
        if (this.state.generatingExport)
            return;

        await this.setStateAsync({ ...this.state, generatingExport: true });

        const urlQuery = this.getUrlQueryObject();
        const eventId = this.props.eventId;
        let abstractIds = this.state.selectedIds;

        // if filtering by search and no selected abstract IDs, get
        // all abstract IDs from search results
        if (this.state.tableSearchResults && !abstractIds.length)
            abstractIds = this.state.tableSearchResults.map((x: IRow) => x.id);

        const body = {
            abstractIds,
            classificationIds: urlQuery.classificationIds,
            eventId,
            groupIds: urlQuery.groupIds,
            invitationIds: urlQuery.invitationIds,
            placementIds: urlQuery.placementIds,
            statusIds: urlQuery.statusIds,
            typeIds: urlQuery.typeIds,
        };

        const result = await this.props.api.post(Api.endpoints.admin.abstract.exportAbstractGrades.withUrlParams({ eventId }), {
            body: JSON.stringify(body),
        });

        if (!this._isMounted)
            return;

        await this.setStateAsync({ ...this.state, generatingExport: false });
        this.getExportFile(result);
    }

    private async generateExportOfReviewers() {
        if (this.state.generatingExport)
            return;

        await this.setStateAsync({ ...this.state, generatingExport: true });

        const urlQuery = this.getUrlQueryObject();
        const eventId = this.props.eventId;
        let abstractIds = this.state.selectedIds;

        // if filtering by search and no selected abstract IDs, get
        // all abstract IDs from search results
        if (this.state.tableSearchResults && !abstractIds.length)
            abstractIds = this.state.tableSearchResults.map((x: IRow) => x.id);

        const body = {
            abstractIds,
            classificationIds: urlQuery.classificationIds,
            eventId,
            groupIds: urlQuery.groupIds,
            invitationIds: urlQuery.invitationIds,
            placementIds: urlQuery.placementIds,
            statusIds: urlQuery.statusIds,
            typeIds: urlQuery.typeIds,
        };

        const result = await this.props.api.post(Api.endpoints.admin.abstract.exportAbstractReviewers.withUrlParams({ eventId }), {
            body: JSON.stringify(body),
        });

        if (!this._isMounted)
            return;

        await this.setStateAsync({ ...this.state, generatingExport: false });
        this.getExportFile(result);
    }

    private async generateExportToPDF() {
        if (this.state.generatingExport)
            return;

        await this.setStateAsync({ ...this.state, generatingExport: true });

        const urlQuery = this.getUrlQueryObject();
        const eventId = this.props.eventId;
        let abstractIds = this.state.selectedIds;

        // if filtering by search and no selected abstract IDs, get
        // all abstract IDs from search results
        if (this.state.tableSearchResults && !abstractIds.length)
            abstractIds = this.state.tableSearchResults.map((x: IRow) => x.id);

        const body = {
            abstractIds,
            classificationIds: urlQuery.classificationIds,
            eventId,
            groupIds: urlQuery.groupIds,
            invitationIds: urlQuery.invitationIds,
            placementIds: urlQuery.placementIds,
            statusIds: urlQuery.statusIds,
            typeIds: urlQuery.typeIds,
        };

        const result = await this.props.api.post(Api.endpoints.admin.abstract.exportAbstractsToPDF.withUrlParams({ eventId }), {
            body: JSON.stringify(body),
        });

        if (!this._isMounted)
            return;

        await this.setStateAsync({ ...this.state, generatingExport: false });
        this.getExportFile(result);
    }

    private getExportFile(result: M.Result.IResult<string>) {
        if (result.isSuccess && result.data) {
            const endpoint =  Api.endpoints.file.tempFile.withUrlParams({ token: result.data });
            const path = Settings.ApiUrl + ApiEndpoint.getFormattedUrl(endpoint) + "?access_token=Bearer%20" + encodeURIComponent(this.props.authToken);
            DocumentHelper.postForm(path, {});
        }
        else
            this.props.notifyError(M.Result.Result.errorsOrMessage(result).join("<br />"));
    }

    private getFilterTypeName(type: FilterType) {
        switch (type) {
            case "class": return "Classification";
            case "group": return "Grading group";
            case "invitation": return "Invitation";
            case "place": return "Placement";
            case "status": return "Status";
            case "type": return "Cagegory";
            default: return "Unknown";
        }
    }

    private getUrlQueryObject(): UrlQueryObject {
        const urlQuery = QueryString.parse(this.props.location.search) as UrlQuery;
        return {
            classificationIds: (urlQuery.class || ",").split(",").filter((x) => x !== "").map((x) => parseInt(x, 10)),
            groupIds: (urlQuery.group || "").split(",").filter((x) => x !== "").map((x) => parseInt(x, 10)),
            invitationIds: (urlQuery.invitation || "").split(",").filter((x) => x !== "").map((x) => parseInt(x, 10)),
            placementIds: (urlQuery.place || "").split(",").filter((x) => x !== "").map((x) => parseInt(x, 10)),
            statusIds: (urlQuery.status || "").split(",").filter((x) => x !== "").map((x) => parseInt(x, 10)),
            typeIds: (urlQuery.type || "").split(",").filter((x) => x !== "").map((x) => parseInt(x, 10)),
        };
    }

    private async handleAbstractsBatchDelete() {
        if (this.batchDeleteConfirm === null)
            return;

        const abstracts = this.props.abstracts.filter((x: IRow) => this.state.selectedIds.indexOf(x.id) > -1);
        if (abstracts.size) {
            const guids = abstracts.map((x: IRow) => x.guid).toArray();
            const result = await this.props.api.delete(Api.endpoints.admin.abstract.batchDelete, {
                body: JSON.stringify(guids),
            });

            if (!this._isMounted)
                return;

            if (this.batchDeleteConfirm)
                this.batchDeleteConfirm.close();

            if (result.isSuccess) {
                await this.clearSelectedAbstractIds();
                this.props.removeAbstracts(guids);
                this.props.notifySuccess(`${result.data} Abstract${result.data === 1 ? " has" : "s have"} been successfully deleted.`);
            }
            else {
                this.props.notifyError(M.Result.Result.errorsOrMessage(result).join("<br />"));
            }
        }
    }

    private async initializeComponent() {
        const eventId = this.props.eventId;

        await this.initializeEvent();
        await this.loadAbstractsFromState();

        this.props.loadAbstracts(eventId);
    }

    private async initializeEvent() {
        const evt = this.props.event;

        if (evt) {
            this.classMap = Utility.createNumberIndexMap(evt.classifications, (x) => x.id);
            this.groupMap = Utility.createNumberIndexMap(evt.gradingGroups, (x) => x.id);
            this.placeMap = Utility.createNumberIndexMap(evt.placements, (x) => x.id);
            this.typeMap = Utility.createNumberIndexMap(evt.types, (x) => x.id);

            await this.setStateAsync({
                ...this.state,
                classifications: evt.classifications,
                gradingGroups: evt.gradingGroups,
                placements: evt.placements,
                types: evt.types,
            });
        }

        await this.updateAllFilterChecks(this.props.location.search);
        await this.updateFilterCounts();
    }

    private isAuthorsHidden() {
        const urlQuery = QueryString.parse(this.props.location.search) as UrlQuery;
        return !!urlQuery.noAuthors;
    }

    private async loadAbstractsFromState(toggleLoading: boolean = false) {
        const eventId = this.props.eventId;
        const abstracts = this.props.abstracts.filter((x: M.Abstract.AbstractListItem) => x.eventId === eventId).toList();

        if (!abstracts.size && toggleLoading)
            this.props.toggleAbstractsLoading(true);

        let srList = List();
        if (this.state.tableSearchResults) {
            for (const x of this.state.tableSearchResults) {
                const a = abstracts.find((_a: M.Abstract.AbstractListItem) => _a.id === x.id);
                if (a)
                    srList = srList.push(a);
            }
        }

        const tableSearchResults = srList.size
            ? srList.toArray()
            : undefined;

        await this.setStateAsync({ ...this.state, abstracts, tableSearchResults });
        await this.filterAbstracts();
        await this.updateFilterCounts();

        // update table state from location state if available
        this.updateTableState(abstracts);
    }

    private onDeleteClick() {
        if (!this.state.selectedIds.length) {
            this.props.notify("Select one or more abstracts to delete", "info", {
                el: document.getElementById("batch-delete-btn") as HTMLElement,
                anchorType: "top",
            });
        }
        else if (this.batchDeleteConfirm) {
            this.batchDeleteConfirm.show(this.state.selectedIds);
        }
    }

    private async onAbstractClassUpdated(abstractIds: number[], classificationId?: number) {
        const abstracts = this.state.abstracts.toList();
        const map = this.classMap[classificationId || 0];
        abstracts.map((a: IRow) => {
            if (abstractIds.indexOf(a.id) > -1) {
                a.classification = map ? map.name : undefined;
                a.classificationId = classificationId;
                return;
            }
        });

        const tableSearchResults = this.state.tableSearchResults;
        if (tableSearchResults) {
            tableSearchResults.map((a: IRow) => {
                if (abstractIds.indexOf(a.id) > -1) {
                    a.classification = map ? map.name : undefined;
                    a.classificationId = classificationId;
                    return;
                }
            });
        }

        await this.setStateAsync({ ...this.state, abstracts, tableSearchResults, loading: false });
        await this.updateFilterCounts(["class"]);
        await this.setStateAsync({ ...this.state, doFilter: true });
        const countLang = abstractIds.length > 1 ? `${abstractIds.length} abstracts` : `abstract #${abstractIds}`;
        this.props.notifySuccess(`Classification of ${countLang} has been updated to <strong>${(map) ? map.name : "None"}</strong>`);
    }

    private async onAbstractGroupUpdated(abstractIds: number[], groupId?: number) {
        const abstracts = this.state.abstracts.toList();
        const map = this.groupMap[groupId || 0];
        abstracts.map((a: IRow) => {
            if (abstractIds.indexOf(a.id) > -1) {
                a.gradingGroup = map ? map.name : undefined;
                a.gradingGroupId = groupId;
                return;
            }
        });

        const tableSearchResults = this.state.tableSearchResults;
        if (tableSearchResults) {
            tableSearchResults.map((a: IRow) => {
                if (abstractIds.indexOf(a.id) > -1) {
                    a.gradingGroup = map ? map.name : undefined;
                    a.gradingGroupId = groupId;
                    return;
                }
            });
        }

        await this.setStateAsync({ ...this.state, abstracts, tableSearchResults, loading: false });
        await this.updateFilterCounts(["group"]);
        await this.setStateAsync({ ...this.state, doFilter: true });
        const countLang = abstractIds.length > 1 ? `${abstractIds.length} abstracts` : `abstract #${abstractIds}`;
        this.props.notifySuccess(`Grading group of ${countLang} has been updated to <strong>${(map) ? map.name : "None"}</strong>`);
    }

    private async onAbstractPlaceUpdated(abstractIds: number[], placementId?: number) {
        const abstracts = this.state.abstracts.toList();
        const map = this.placeMap[placementId || 0];
        abstracts.map((a: IRow) => {
            if (abstractIds.indexOf(a.id) > -1) {
                a.placement = map ? map.name : undefined;
                a.placementId = placementId;
                return;
            }
        });

        const tableSearchResults = this.state.tableSearchResults;
        if (tableSearchResults) {
            tableSearchResults.map((a: IRow) => {
                if (abstractIds.indexOf(a.id) > -1) {
                    a.placement = map ? map.name : undefined;
                    a.placementId = placementId;
                    return;
                }
            });
        }

        await this.setStateAsync({ ...this.state, abstracts, tableSearchResults, loading: false });
        await this.updateFilterCounts(["place"]);
        await this.setStateAsync({ ...this.state, doFilter: true });
        const countLang = abstractIds.length > 1 ? `${abstractIds.length} abstracts` : `abstract #${abstractIds}`;
        this.props.notifySuccess(`Placement of ${countLang} has been updated to <strong>${(map) ? map.name : "None"}</strong>`);
    }

    private async onAbstractsScheduled(abstractIds: number[], evt: M.Event.SubEvent) {
        this.props.notifySuccess(`${abstractIds.length} abstract${abstractIds.length === 1 ? " has" : "s have"} been successfully scheduled.`);
        this.props.loadAbstracts(this.props.eventId);
    }

    private async onAbstractStatusUpdated(abstractIds: number[], statusId?: number) {
        const abstracts = this.state.abstracts.toList();
        const status = this.statusMap[statusId || 0];
        abstracts.map((a: M.Abstract.AbstractListItem) => {
            if (abstractIds.indexOf(a.id) > -1) {
                a.statusId = statusId || 0;
                return;
            }
        });

        const tableSearchResults = this.state.tableSearchResults;
        if (tableSearchResults) {
            tableSearchResults.map((a: IRow) => {
                if (abstractIds.indexOf(a.id) > -1) {
                    a.statusId = statusId || 0;
                    return;
                }
            });
        }

        await this.setStateAsync({ ...this.state, abstracts, tableSearchResults, loading: false });
        await this.updateFilterCounts(["status"]);
        await this.setStateAsync({ ...this.state, doFilter: true });
        const countLang = abstractIds.length > 1 ? `${abstractIds.length} abstracts` : `abstract #${abstractIds}`;
        this.props.notifySuccess(`Status of ${countLang} has been updated to <strong>${(status) ? status.name : "None"}</strong>`);
    }

    private onAssignGroupClick() {
        if (!this.state.selectedIds.length) {
            this.props.notify("Select one or more abstracts to assign to grading groups", "info", {
                el: document.getElementById("assign-group-btn") as HTMLElement,
                anchorType: "top",
            });
        }
        else if (this.groupUpdateModal && this.props.event) {
            const groups = this.props.event.gradingGroups;
            this.groupUpdateModal.show(groups, this.state.selectedIds);
        }
    }

    /**
     * Event handler when filter item is [un]checked
     * @param id ID of the filter
     * @param checked Whether filter was [un]checked
     * @param type The filter type
     */
    private async onFilterCheck(id: string | number, checked: boolean, type: FilterType) {
        const urlQuery = QueryString.parse(this.props.location.search);
        let filterIds = Utility.commaStringToIntArray(urlQuery[type]);

        if (type === "status") {
            if (!checked)
                filterIds = [];
            else
                filterIds = [parseInt(id.toString(), 10)];
        }
        else {
            Utility.addToOrRemoveFromArray(filterIds, id, checked);
        }

        this.props.replaceUrlQuery({ [type]: filterIds.join(",") });
        this.saveTableState();
    }

    private onScheduleAbstractsClick() {
        if (!this.state.selectedIds.length) {
            this.props.notify("Select one or more abstracts to be scheduled", "info", {
                el: document.getElementsByClassName("react-bs-select-all")[0] as HTMLElement,
                anchorType: "top-left",
            });
        }
        else if (this.scheduleAbstractsModal)
            this.scheduleAbstractsModal.show(this.state.selectedIds);
    }

    private onUpdateStatusClick() {
        if (!this.state.selectedIds.length) {
            this.props.notify("Select one or more abstracts to update their status", "info", {
                el: document.getElementsByClassName("react-bs-select-all")[0] as HTMLElement,
                anchorType: "top-left",
            });
        }
        else if (this.statusUpdateModal && this.props.event) {
            const statusItems = this.props.event.statuses;
            this.statusUpdateModal.show(statusItems, this.state.selectedIds);
        }
    }

    private toggleAuthorsColumn() {
        const authorsHidden = this.isAuthorsHidden();
        this.props.replaceUrlQuery({ ["noAuthors"]: authorsHidden ? "" : "1" });
        this.saveTableState();
    }

    private async updateAllFilterChecks(query?: string) {
        // get filtering maps from state
        const classifications = [ ...this.state.classifications ];
        const gradingGroups = [ ...this.state.gradingGroups ];
        const invitationResponses = [ ...this.state.invitationResponses ];
        const placements = [ ...this.state.placements ];
        const statuses = [ ...this.state.statuses ];
        const types = [ ...this.state.types ];
        const appliedFilters: M.Search.AppliedFilter[] = [];

        this.updateFilterChecks(types, "type", appliedFilters, query);
        this.updateFilterChecks(statuses, "status", appliedFilters, query);
        this.updateFilterChecks(placements, "place", appliedFilters, query);
        this.updateFilterChecks(classifications, "class", appliedFilters, query);
        this.updateFilterChecks(gradingGroups, "group", appliedFilters, query);
        this.updateFilterChecks(invitationResponses, "invitation", appliedFilters, query);

        await this.setStateAsync({
            ...this.state,
            appliedFilters,
            classifications,
            gradingGroups,
            invitationResponses,
            placements,
            selectedIds: [],
            statuses,
            types,
        });

        await this.setStateAsync({
            ...this.state,
            doFilter: true,
        });

        await this.updateFilterCounts();
    }

    /**
     * Resets checked filters based on current url query
     */
    private updateFilterChecks(filters: M.Search.SearchFilter[], type: FilterType, appliedFilters: M.Search.AppliedFilter[], query?: string) {
        query = query === undefined ? this.props.location.search : query;
        const urlQuery = QueryString.parse(query);
        const itemIds = Utility.commaStringToIntArray(urlQuery[type]);

        for (const item of filters) {
            item.checked = itemIds.indexOf(item.id) > -1;
            if (item.checked) {
                appliedFilters.push({ id: item.id, name: item.name, type, typeName: this.getFilterTypeName(type) });

                if (type === "status")
                    break;
            }
        }
    }

    private async updateFilterCounts(forFilters?: FilterType[]) {
        // get filtering maps from state
        const classifications = [ ...this.state.classifications ];
        const gradingGroups = [ ...this.state.gradingGroups ];
        const invitationResponses = [ ...this.state.invitationResponses ];
        const placements = [ ...this.state.placements ];
        const statuses = [ ...this.state.statuses ];
        const types = [ ...this.state.types ];

        const list = this.state.tableSearchResults ? List(this.state.tableSearchResults) : this.state.abstracts;

        // reset counts
        if (!forFilters || forFilters.indexOf("class") > -1)
            for (const f of classifications)
                f.count = list.filter((a: IRow) => f.id === a.classificationId).size;

        if (!forFilters || forFilters.indexOf("group") > -1)
            for (const f of gradingGroups)
                f.count = list.filter((a: IRow) => f.id === a.gradingGroupId).size;

        if (!forFilters || forFilters.indexOf("invitation") > -1)
            for (const f of invitationResponses)
                f.count = list.filter((a: IRow) => f.id === (a.invitationResponseId || 0) && !!a.invitationSent).size;

        if (!forFilters || forFilters.indexOf("place") > -1)
            for (const f of placements)
                f.count = list.filter((a: IRow) => f.id === a.placementId).size;

        if (!forFilters || forFilters.indexOf("status") > -1)
            for (const f of statuses)
                f.count = list.filter((a: IRow) => f.id === a.statusId).size;

        if (!forFilters || forFilters.indexOf("type") > -1)
            for (const f of types)
                f.count = list.filter((a: IRow) => f.id === a.typeId).size;

        await this.setStateAsync({
            ...this.state,
            classifications,
            gradingGroups,
            invitationResponses,
            placements,
            statuses,
            types,
        });
    }
}

export const AbstractList: React.ComponentClass<OwnProps> = connect(mapStateToProps, mapDispatchToProps)(withRouter(ListComponent) as any);



// WEBPACK FOOTER //
// ./src/containers/adminAbstracts/list/List.tsx